// AI Generated Component
import { Component, Input, OnInit } from '@angular/core';
import { FastTrackService } from '@client/app/modules/fast-track/services/fast-track/fasttrack.service';
import { SessionService } from '@monsido/core/session/session.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { MonModalService } from '@client/app/modules/modals/services/mon-modal/mon-modal.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { AccessibilityAutofixService } from '@client/app/modules/page-fix/services/accessibility-auto-fix/accessibility-auto-fix.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { FastTrackDialogsPagesComponent } from '@client/app/modules/accessibility/dialogs/fast-track-pages/fast-track-pages.component';
import { safeClone } from '@client/app/shared/utils/object-utils';
import { PipeService } from '@client/app/filters/pipe-transformation.service';

@Component({
    selector: 'page-details-section-accessibility-information-fast-track',
    standalone: false,
    template: `
        <div class="page-details-section-accessibility-information-fast-track">
            <div class="mon-dialog-header">
                <page-header
                    [header]="'Fast track html snippet' | translate"
                    panelRightClass="col-48 col-sm-18"
                    panelLeftClass="col-48 col-sm-30 pt-2">
                    <page-header-content>
                        <div class="d-flex justify-content-end pt-3">
                            <button
                                [monModalPrompt]="('Are you sure you want mark all checks as reviewed/ignored for this fast-track?' | translate) + '<br><br>' + ('Add reason(Optional)' | translate)"
                                (monModalPromptAction)="onMarkAllAsFixed($event)"
                                monEventTrigger="actiontrigger"
                                [monEventParams]="{action:'fasttrack-review-all'}"
                                class="btn btn-border-radius btn-secondary"
                                *ngIf="!ignoringAll"
                                [disabled]="isAllIgnored || checks.length === 0">
                                {{ 'Mark all as reviewed/ignored' | translate }}
                            </button>
                            <button
                                class="btn btn-border-radius btn-secondary"
                                [disabled]="true"
                                *ngIf="ignoringAll">
                                <span class="fas fa-spin fa-spinner" aria-hidden="true"></span>
                                <span>{{ 'In progress of ignoring/reviewing all' | translate }}</span>
                            </button>
                        </div>
                    </page-header-content>
                </page-header>
            </div>
            <div class="mon-dialog-body bg-white" *ngIf="accessibility">
                <div class="row">
                    <div class="col-48 border-bottom">
                        <div class="px-4">
                            <div class="row align-items-stretch">
                                <div class="col-18 fast-track-check-status py-2">
                                    <p class="mb-0">{{ 'Effect on overall compliance level' | translate }}</p>
                                    <span class="font-size-1-5">{{ (accessibility.effect_on_compliance/100) | monNumber:'0.[00]%' }}</span>
                                </div>
                                <div class="col-16 border-size-1 border-l border-light-grey fast-track-check-status py-2">
                                    <div class="doughnut-pages" *ngIf="accessibility.id && data">
                                        <mon-chart-doughnut
                                            [options]="options"
                                            [data]="data"
                                            [labels]="labels"
                                            [colors]="colors">
                                        </mon-chart-doughnut>
                                    </div>
                                    <div class="inline-block ml-2">
                                        <p class="mb-0">{{ 'Affected pages' | translate }}</p>
                                        <span class="font-size-1-5">{{ getAffectedPages() }}</span>
                                    </div>
                                </div>
                                <div class="col-14 border-size-1 border-l border-light-grey fast-track-check-status py-2">
                                    <p class="mb-0">{{ 'Failing checks' | translate }}</p>
                                    <span class="font-size-1-5">{{ accessibility.caused_issues }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-lg-48 pt-2">
                        <div class="py-2 px-4">
                            <span>{{ 'HTML snippet' | translate }}</span>
                            <span *ngIf="isAllIgnored">{{ '(The HTML snippet is now Marked as Reviewed/Ignored)' | translate }}</span>
                            <pre class="html-snippet my-2" [ngClass]="{'info': isAllIgnored}">{{ sourceCode | monCharacterLimit:maxCharacters }}</pre>
                            <a (click)="toggleCharacterLimit()"
                               role="button"
                               *ngIf="accessibility.source_code && maxCharacters !== 0 && accessibility.source_code.length > maxCharacters"
                               [attr.aria-label]="'Show the full code snippet' | translate">
                                {{ 'Show more' | translate }}
                            </a>
                            <a (click)="toggleCharacterLimit()"
                               role="button"
                               *ngIf="maxCharacters === 0"
                               [attr.aria-label]="'Show less of the code snippet' | translate">
                                {{ 'Show less' | translate }}
                            </a>
                        </div>
                    </div>
                    <div class="col-lg-48">
                        <mon-spinner [loading]="ignoringAll">
                            <table-container [collection]="checks" [progress]="promise">
                                <table class="table table-fixed table-hover remove-table-header-bottom-border">
                                    <thead>
                                    <tr>
                                        <th class="pl-4 col-lg-3 col-md-3">{{ 'Level' | translate }}</th>
                                        <th>{{ 'Check' | translate }}</th>
                                        <th class="col-md-10 col-lg-9 text-center" *ngIf="showAutofix">{{ 'PageCorrect' | translate }}</th>
                                        <th class="col-lg-7 col-md-8 text-center">{{ 'Action' | translate }}</th>
                                        <th class="col-lg-5 col-md-7 text-right"></th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr class="cursor-pointer" *ngFor="let content of checks">
                                        <td class="pl-4 text-v-middle">
                                            <div class="bubble" [ngClass]="bubbleClass(content.issue_abbr)">
                                                {{ content.issue_abbr }}
                                            </div>
                                        </td>
                                        <td class="text-v-middle">
                                            <div [innerHTML]="content.name"></div>
                                            <div>
                                                <span class="fas fa-book" aria-hidden="true"></span>
                                                <span translate [translate-args]="{issueNumber: content.issue_number}">Part of success criteria [[issueNumber]]</span>
                                                <acc-help-center-btn [check]="content"></acc-help-center-btn>
                                            </div>
                                        </td>
                                        <td class="text-center text-v-middle" *ngIf="showAutofix">
                                            <page-fix-icon
                                                (cb)="getChecks()"
                                                [type]="content.getPageFixType()"
                                                [typeId]="content.getPageFixFixableId(accessibility.id)"
                                                [model]="content"
                                                *ngIf="content.page_fix_fixable">
                                            </page-fix-icon>
                                        </td>
                                        <td class="text-center text-v-middle">
                                            <mon-action-menu [monActionMenuItems]="content.dropdown">
                                                <span>{{ 'Action' | translate }}</span>
                                            </mon-action-menu>
                                        </td>
                                        <td class="pr-4 text-right">
                                            <pages-button
                                                (callback)="openPages(content)"
                                                [ngModel]="content.pages_count"
                                                name="pagesCount">
                                            </pages-button>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </table-container>
                        </mon-spinner>
                        <div class="mt-3" *ngIf="ignoringAll">
                            <ngb-progressbar
                                class="progress-striped active"
                                type="success"
                                [max]="checks.length"
                                [value]="ignoringDone">
                            </ngb-progressbar>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `,
})
export class PageDetailsSectionAccessibilityInformationFastTrackComponent implements OnInit {
    @Input() sourceCodeId: string = '';

    accessibility: any;
    checks: any[] = [];
    ignoringAll = false;
    isAllIgnored = false;
    ignoringDone = 0;
    sourceCode = '';
    labels = ['', ''];
    colors = ['#ce2e54', '#dcdcdc'];
    options = {
        cutoutPercentage: 70,
        tooltips: {
            enabled: false,
        },
    };
    data: any = null;
    maxCharacters: number = 0;
    showAutofix: boolean = false;
    promise: any;

    private snippetCharacterLimit = 200;

    constructor (
        private fastTrackService: FastTrackService,
        private sessionService: SessionService,
        private monEventService: MonEventService,
        private monModalService: MonModalService,
        private translateService: TranslateService,
        private accessibilityAutofixService: AccessibilityAutofixService,
        private activeFeatureService: ActiveFeatureService,
        private pipeService: PipeService,
    ) {}

    ngOnInit (): void {
        this.maxCharacters = this.snippetCharacterLimit;
        this.showAutofix =
            this.activeFeatureService.isFeatureActive('page_fix_accessibility_check') ||
            this.activeFeatureService.isFeatureActive('page_fix_accessibility_source_code');

        this.getSummary().then(() => {
            this.getChecks();
        }).catch(() => {
            // Handle error silently
        });
    }

    bubbleClass (issueAbbr: string): string {
        // Implementation for bubble class logic
        switch (issueAbbr) {
            case 'A':
                return 'bubble-error';
            case 'AA':
                return 'bubble-warning';
            case 'AAA':
                return 'bubble-info';
            default:
                return 'bubble-default';
        }
    }

    private getSummary (): Promise<any> {
        return this.fastTrackService.getFastTrack(this.sourceCodeId).then((data: any) => {
            this.accessibility = data;
            if (data.source_code) {
                this.sourceCode = this.accessibility.source_code;
            }
        });
    }

    getChecks (): void {
        this.promise = this.fastTrackService.getAllChecks(this.sourceCodeId).then((data: any[]) => {
            this.isAllIgnored = Array.isArray(data) && data.length === 0;
            const affectedPages = this.getAffectedPagesCount(data);
            this.data = [affectedPages, (this.sessionService.domain?.crawled_pages || 0) - affectedPages];

            if (this.showAutofix) {
                return this.accessibilityAutofixService.parseChecks(data, this.accessibility).then((checks) => {
                    this.checks = checks;
                    this.updateDropdowns();
                });
            } else {
                this.checks = data;
                return this.updateDropdowns();
            }
        }).catch(() => {
            // Handle error silently
        });
    }

    private updateDropdowns (): void {
        for (const check of this.checks) {
            const dropdown = [
                {
                    label: this.translateService.getString('Review'),
                    action: () => this.markAsFixed(check, true),
                    eventParams: { action: 'fasttrack-review' },
                    shouldShow: check.result_type === 'review',
                },
                {
                    label: this.translateService.getString('Ignore'),
                    action: () => this.markAsFixed(check, true),
                    eventParams: { action: 'fasttrack-ignore' },
                    shouldShow: check.result_type === 'error' || check.result_type === 'warning',
                },
                {
                    label: this.translateService.getString('Mark check as fixed'),
                    action: () => this.markCheckAsFixed(check),
                    eventParams: { action: 'fasttrack-mark-fixed' },
                    shouldShow: check.result_type === 'error' || check.result_type === 'warning',
                },
            ];

            check.dropdown = dropdown.filter((option) => option.shouldShow !== false).map(item => {
                return {
                    label: item.label,
                    action: () => {
                        this.monEventService.run('actionTrigger', item.eventParams);
                        item.action();
                    },
                };
            });
        }
    }

    private markAsFixed (check: any, updateChecks: boolean): Promise<any> {
        let confirmText = ' <strong>' + check.name + '</strong>';
        confirmText += '<br /><br />' + this.translateService.getString('Add reason (Optional)');

        return this.monModalService.prompt(confirmText, undefined).then((result: any) => {
            const data = safeClone(check);
            data.comment = result;
            data.ignored_reviewed = true;
            return this.updateCheck(data).then(() => {
                if (updateChecks === true) {
                    this.getChecks();
                    this.getSummary();
                }
            }).catch(() => {
                // Handle error silently
            });
        });
    }

    private markCheckAsFixed (content: any): void {
        const message = this.translateService.getString('Are you sure you want to mark this check as fixed on all pages?');
        this.monModalService.confirm(message).then(() => {
            this.fastTrackService.markCheckAsFixed(
                this.sessionService.domain?.id as any,
                this.accessibility.id,
                content.id,
            ).then(() => {
                setTimeout(() => {
                    this.getChecks();
                    this.getSummary();
                }, 500);
            }).catch(() => {
                // Handle error silently
            });
        });
    }

    onMarkAllAsFixed (val: any): void {
        this.ignoringAll = true;
        this.ignoringDone = 0;
        const result = this.markAllAsFixed(safeClone(this.checks), 0, val);
        result
            .then(
                () => {},
                (errorsCount: number) => {
                    this.monModalService.alert(
                        '' +
                            errorsCount +
                            ' ' +
                            this.translateService.getString(
                                'failed when trying to ignore/review all. please try agin, or contact the Acquia Web Governance team',
                            ),
                    );
                },
            )
            .finally(() => {
                this.getChecks();
                this.getSummary();
                this.ignoringAll = false;
            });
    }

    private markAllAsFixed (checks: any[], errorsCount: number, data: any): Promise<any> {
        if (checks[0]) {
            const head = safeClone(checks[0]);
            const tail = checks.splice(1);
            head.comment = data;
            head.ignored_reviewed = true;
            return this.updateCheck(head)
                .then(
                    () => {},
                    () => {
                        errorsCount = errorsCount + 1;
                    },
                )
                .finally(() => {
                    this.ignoringDone = this.ignoringDone + 1;
                    if (tail.length > 0) {
                        return this.markAllAsFixed(tail, errorsCount, data);
                    } else {
                        return this.markAllAsFixedDone(errorsCount);
                    }
                });
        } else {
            return this.markAllAsFixedDone(errorsCount);
        }
    }

    private markAllAsFixedDone (errorsCount: number): Promise<any> {
        return new Promise((resolve, reject) => {
            if (errorsCount > 0) {
                reject(errorsCount);
            } else {
                resolve(undefined);
            }
        });
    }

    private getAffectedPagesCount (checks: any[]): number {
        if (Array.isArray(checks)) {
            return checks.reduce((num: number, check: any) => {
                return Math.max(check.pages_count, num);
            }, 0);
        }
        return 0;
    }

    getAffectedPages (): string {
        const count = this.getAffectedPagesCount(this.checks);

        if (count < 10) {
            return this.translateService.getString('Less than 10');
        } else if (count < 50) {
            return this.translateService.getString('Less than 50');
        } else if (count < 100) {
            return this.translateService.getString('Less than 100');
        } else if (count < 1000) {
            return this.translateService.getString('More than ') + this.pipeService.get('monNumber')(count / 1000, 1) + 'k';
        } else {
            return this.translateService.getString('More than ') + this.pipeService.get('monNumber')(count, '0a');
        }
    }

    openPages (check: any): void {
        const params = {
            body: FastTrackDialogsPagesComponent,
            size: 'lg',
            data: {
                accessibilitySourceCodeId: this.sourceCodeId,
                check: check,
            },
        };

        this.monEventService.run(MON_EVENTS.LOAD_DIALOG, { params, callback: () => this.getChecks() });
    }

    private updateCheck (check: any): Promise<any> {
        return this.fastTrackService.updateCheck(this.sourceCodeId, check);
    }

    toggleCharacterLimit (): void {
        if (this.maxCharacters > 0) {
            this.maxCharacters = 0;
        } else {
            this.maxCharacters = this.snippetCharacterLimit;
        }
    }
}
